import { useSearchParams } from "react-router";

import { Column, TopbarContainer, TopbarTitle } from "@/components";
import { AssistantConversationsTab } from "@/components/tools/assistant/AssistantConversationsTab";
import { AssistantItemsTab } from "@/components/tools/assistant/AssistantItemsTab";
import { AssistantOverviewTab } from "@/components/tools/assistant/AssistantOverviewTab";
import { AssistantRunsTab } from "@/components/tools/assistant/AssistantRunsTab";
import { AssistantSchedulesTab } from "@/components/tools/assistant/AssistantSchedulesTab";
import { DAY_OPTIONS } from "@/components/tools/assistant/format";
import { Legend } from "@/components/tools/assistant/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useSetTitle } from "@/hooks/useSetTitle";

const TABS = ["overview", "conversations", "items", "schedules", "runs"] as const;
type Tab = (typeof TABS)[number];

/**
 * Monitoring for the Staffer assistant (Phoebe): every Staffer's thread, the
 * customer emails it surfaced and how they resolved, schedules, and job runs.
 * Admin and product users only; guac enforces it.
 */
export const ToolsAssistantPage = () => {
  // Tab, thread, and window live in the URL so a view can be linked.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: Tab = TABS.includes(tabParam as Tab) ? (tabParam as Tab) : "overview";
  const threadId = searchParams.get("thread") ?? undefined;
  const days = Number(searchParams.get("days")) || 7;

  useSetTitle({ title: "Tools | Assistant" });

  const update = (next: Record<string, string>) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(next)) params.set(key, value);
        return params;
      },
      { replace: true },
    );
  };

  const openThread = (id: string) => {
    update({ tab: "conversations", thread: id });
  };

  return (
    <Column className="h-svh">
      <TopbarContainer>
        <TopbarTitle>Assistant</TopbarTitle>
        <Select
          onValueChange={(value) => {
            update({ days: value });
          }}
          value={days.toString()}
        >
          <SelectTrigger className="w-[160px] rounded-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DAY_OPTIONS.map((d) => (
              <SelectItem key={d} value={d.toString()}>
                Last {d} {d === 1 ? "day" : "days"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TopbarContainer>
      <Tabs
        className="flex min-h-0 flex-1 flex-col gap-4 p-6"
        onValueChange={(value) => {
          update({ tab: value });
        }}
        value={tab}
      >
        <TabsList className="self-start">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="conversations">Conversations</TabsTrigger>
          <TabsTrigger value="items">Emails</TabsTrigger>
          <TabsTrigger value="schedules">Schedules</TabsTrigger>
          <TabsTrigger value="runs">Runs</TabsTrigger>
        </TabsList>
        <Legend />
        <TabsContent
          className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
          value="overview"
        >
          <AssistantOverviewTab days={days} onOpenThread={openThread} />
        </TabsContent>
        <TabsContent
          className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
          value="conversations"
        >
          <AssistantConversationsTab
            days={days}
            onSelectThread={(id) => {
              update({ thread: id });
            }}
            threadId={threadId}
          />
        </TabsContent>
        <TabsContent
          className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
          value="items"
        >
          <AssistantItemsTab days={days} onOpenThread={openThread} />
        </TabsContent>
        <TabsContent
          className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
          value="schedules"
        >
          <AssistantSchedulesTab onOpenThread={openThread} />
        </TabsContent>
        <TabsContent
          className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
          value="runs"
        >
          <AssistantRunsTab days={days} />
        </TabsContent>
      </Tabs>
    </Column>
  );
};
